// Revolt Dropdown Button Component
// Button with dropdown arrow that opens a dropdown menu below
// Used for buttons that need additional options (like refresh with auto-refresh toggle)

import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import ReactDOM from 'react-dom';
import { RevoltIcon } from './RevoltIcon';
import './RevoltDropdownButton.css';

const MENU_GAP = 4;

export function RevoltDropdownButton({
  children,
  dropdownContent,
  onClick,
  onArrowClick,
  disabled = false,
  className = '',
  showDropdown = true,
  ...props
}) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  const updateDropdownPosition = useCallback(() => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    // Always try to open downward first, fallback to upward if needed
    const estimatedHeight = 80; // Rough estimate for dropdown height
    const shouldOpenUp = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;

    const top = shouldOpenUp
      ? Math.max(8, rect.top - estimatedHeight - MENU_GAP)
      : rect.bottom + MENU_GAP;

    setDropdownPosition({
      top,
      left: Math.max(8, rect.left),
      width: rect.width,
    });
  }, []);

  const handleContentClick = useCallback((e) => {
    if (disabled) return;
    e.stopPropagation();
    onClick?.(e);
  }, [disabled, onClick]);

  const handleDropdownClick = useCallback((e) => {
    if (disabled || !showDropdown) return;
    e.stopPropagation();

    if (isOpen) {
      setIsOpen(false);
    } else {
      updateDropdownPosition();
      setIsOpen(true);
    }

    onArrowClick?.(e);
  }, [disabled, showDropdown, isOpen, updateDropdownPosition, onArrowClick]);

  const handleClickOutside = useCallback((e) => {
    if (
      buttonRef.current &&
      !buttonRef.current.contains(e.target) &&
      dropdownRef.current &&
      !dropdownRef.current.contains(e.target)
    ) {
      setIsOpen(false);
    }
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, handleClickOutside]);

  // Update position when window resizes
  useEffect(() => {
    if (isOpen) {
      const handleResize = () => {
        updateDropdownPosition();
      };
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [isOpen, updateDropdownPosition]);

  const buttonClasses = [
    'revolt-dropdown-btn',
    showDropdown ? 'has-dropdown' : 'no-dropdown',
    disabled ? 'is-disabled' : '',
    isOpen ? 'is-open' : '',
    className,
  ].filter(Boolean).join(' ');

  const dropdownClasses = [
    'revolt-dropdown-menu',
    isOpen ? 'is-open' : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      <div
        ref={buttonRef}
        className={buttonClasses}
        {...props}
      >
        {/* Main content area - left side */}
        <button
          type="button"
          className="button-content-area"
          onClick={handleContentClick}
          disabled={disabled}
          title={props.title}
        >
          <span className="button-content">{children}</span>
        </button>

        {/* Dropdown trigger area - right side */}
        {showDropdown && (
          <>
            <div className="dropdown-divider" />
            <button
              type="button"
              className="dropdown-trigger-area"
              onClick={handleDropdownClick}
              disabled={disabled}
              aria-label="Toggle dropdown"
            >
              <RevoltIcon name="chevron-down" size="sm" className="dropdown-arrow" />
            </button>
          </>
        )}
      </div>

      {isOpen && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          className={dropdownClasses}
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            zIndex: 1000,
          }}
        >
          {dropdownContent}
        </div>,
        document.body
      )}
    </>
  );
}